import { useMemo, useRef, useState, useCallback } from 'react';
import Card from './Card';
import {
	resolveCollisions,
	compactLayout,
	reflowCardsForColumns,
} from '../utils/gridLayout';
import { useElementContentSize } from '../hooks/useSize';

export default function GridDashboard({
	config,
	cards,
	onCardsChange,
	onUpdateCardData,
	isEditMode,
	currentColumns,
	userStats,
	announcements,
	leaderboards,
	userStatsLoading,
	isAdminView,
	currentUser,
	setCurrentUser,
	departments,
	subCompanies,
	jobNotificationSetting,
	hideSubCompany,
	hotSearchedJobs,
	recognitionShoutouts,
	myRewards,
}) {
	const outerWrapRef = useRef(null);
	const { ref: innerMeasureRef, width: measuredWidth } =
		useElementContentSize();

	const sortedBreakpoints = useMemo(
		() => [...config.breakpoints].sort((a, b) => b.minWidth - a.minWidth),
		[config.breakpoints]
	);

	const desktopColumns = sortedBreakpoints[0]?.columns ?? 4;
	const isDesktopView = currentColumns === desktopColumns;

	const displayCards = useMemo(() => {
		if (isDesktopView) return cards;
		return reflowCardsForColumns(cards, currentColumns);
	}, [cards, currentColumns, isDesktopView]);

	const containerWidth = useMemo(() => {
		const padAdjusted = Math.max(0, measuredWidth);
		return padAdjusted;
	}, [measuredWidth, isEditMode, config.horizontalMargin]);

	const columnWidth = useMemo(() => {
		if (config.responsiveWidth && containerWidth > 0) {
			// When not in edit mode, container has no padding/margin
			// so we only need margins between columns, not on the sides
			const totalMargins = isEditMode
				? config.horizontalMargin * (currentColumns + 1)
				: config.horizontalMargin * (currentColumns - 1);
			const raw = (containerWidth - totalMargins) / currentColumns;
			return Math.max(1, Math.round(raw));
		}
		return config.columnWidth;
	}, [
		config.responsiveWidth,
		config.horizontalMargin,
		currentColumns,
		containerWidth,
		config.columnWidth,
		isEditMode,
	]);

	const [lastResolvedCards, setLastResolvedCards] = useState([]);
	const [dragState, setDragState] = useState(null);
	const [resizeState, setResizeState] = useState(null);

	const handleDragMove = useCallback(
		(id, pixelX, pixelY) => {
			if (!isDesktopView) return;
			const draggedCard = cards.find((c) => c.id === id);
			if (!draggedCard) return;

			const cellWidth = columnWidth + config.horizontalMargin;
			const cellHeight = config.rowHeight + config.verticalMargin;
			const originalCard = dragState?.originalCard || draggedCard;

			if (!dragState) {
				setDragState({
					cardId: id,
					offset: { x: pixelX, y: pixelY },
					previewCards: cards,
					originalCard: draggedCard,
				});
				return;
			}

			const gridDeltaX = Math.round(pixelX / cellWidth);
			const gridDeltaY = Math.round(pixelY / cellHeight);
			const newX = Math.max(
				0,
				Math.min(
					currentColumns - draggedCard.width,
					originalCard.x + gridDeltaX
				)
			);
			const newY = Math.max(0, originalCard.y + gridDeltaY);

			const updatedCard = { ...originalCard, x: newX, y: newY };
			const resolvedCards = resolveCollisions(
				updatedCard,
				cards,
				currentColumns,
				{ x: pixelX, y: pixelY },
				cellWidth,
				cellHeight
			);
			const previewCards = compactLayout(resolvedCards);

			setDragState({
				cardId: id,
				offset: { x: pixelX, y: pixelY },
				previewCards,
				originalCard,
			});
		},
		[
			isDesktopView,
			cards,
			columnWidth,
			config.horizontalMargin,
			config.rowHeight,
			config.verticalMargin,
			dragState,
			currentColumns,
			onUpdateCardData,
		]
	);

	const handleDragEnd = useCallback(
		(id, gridX, gridY) => {
			if (!isDesktopView || !dragState) return;
			const cellWidth = columnWidth + config.horizontalMargin;
			const cellHeight = config.rowHeight + config.verticalMargin;

			const movedCard = { ...dragState.originalCard, x: gridX, y: gridY };
			const finalCards = resolveCollisions(
				movedCard,
				cards,
				currentColumns,
				dragState.offset,
				cellWidth,
				cellHeight
			);
			const compacted = compactLayout(finalCards);
			onCardsChange(compacted, true);
			setDragState(null);
		},
		[
			isDesktopView,
			dragState,
			columnWidth,
			config.horizontalMargin,
			config.rowHeight,
			config.verticalMargin,
			cards,
			currentColumns,
			onCardsChange,
			onUpdateCardData,
		]
	);

	const handleCardResize = useCallback(
		(id, width, height) => {
			if (!isDesktopView) return;
			const resizedCard = cards.find((c) => c.id === id);
			console.log('resizedCard', resizedCard);
			if (!resizedCard) return;

			// Initialize resize state with original cards on first resize event
			if (!resizeState) {
				setResizeState({
					cardId: id,
					originalCards: cards.map((c) => ({ ...c })),
				});
			}

			const updatedCard = { ...resizedCard, width, height };
			const baseCards = resizeState?.originalCards || cards;
			console.log('baseCards', baseCards);
			const resolvedCards = resolveCollisions(
				updatedCard,
				baseCards,
				currentColumns
			);
			setLastResolvedCards(resolvedCards);
			onCardsChange(resolvedCards);
		},
		[
			isDesktopView,
			cards,
			currentColumns,
			onCardsChange,
			resizeState,
			onUpdateCardData,
		]
	);

	const handleResizeStart = useCallback(() => {
		setLastResolvedCards(cards);
	}, [cards]);

	const handleResizeEnd = useCallback(() => {
		console.log('lastResolvedCards onResizeEnd:', lastResolvedCards);
		if (lastResolvedCards.length > 0) onCardsChange(lastResolvedCards, true);
		setResizeState(null);
	}, [lastResolvedCards, onCardsChange, onUpdateCardData]);

	const deleteCard = useCallback(
		(id) => {
			const filtered = cards.filter((c) => c.id !== id);
			const compacted = compactLayout(filtered);
			onCardsChange(compacted, true);
		},
		[cards, onCardsChange]
	);

	const finalDisplayCards = useMemo(() => {
		if (dragState && isDesktopView) return dragState.previewCards;
		return displayCards;
	}, [dragState, displayCards, isDesktopView]);

	const maxRow = useMemo(
		() =>
			finalDisplayCards.reduce((max, c) => Math.max(max, c.y + c.height), 0),
		[finalDisplayCards]
	);
	const containerHeight =
		maxRow * (config.rowHeight + config.verticalMargin) + config.verticalMargin;

	return (
		<div
			className="flex-1 pl-0 pr-0"
			ref={outerWrapRef}
			style={{
				height: `${containerHeight + config.rowHeight + config.verticalMargin}px`,
			}}
		>
			{isEditMode && !isDesktopView && (
				<div
					className="mb-4 mx-auto bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm"
					style={{ width: `${containerWidth}px` }}
				>
					<strong>Preview Mode:</strong> Editing is locked at this screen size.
					Resize to desktop view to edit cards.
				</div>
			)}
			<div ref={innerMeasureRef} className="mx-auto w-full">
				<div
					className="relative"
					style={{
						width: `${containerWidth}px`,
						minHeight: `${Math.max(containerHeight, 150)}px`,
						height: `${containerHeight + config.rowHeight}px`,
						paddingLeft: isEditMode ? `${config.horizontalMargin - 2}px` : 0,
						paddingTop: isEditMode ? `${config.verticalMargin - 2}px` : 0,
						borderRadius: '14px',
						backgroundColor: isEditMode ? '#ffffff6b' : 'transparent',
						borderColor: isEditMode
							? 'oklch(87.2% 0.01 258.338)'
							: 'transparent',
						borderStyle: isEditMode ? 'dashed' : 'none',
						borderWidth: isEditMode ? '2px' : '0px',
						//marginLeft: isEditMode ? '-2px' : 0,
						//marginRight: isEditMode ? '-2px' : 0,
					}}
				>
					{cards.length === 0 && (
						<div className="absolute inset-0 flex items-center justify-center text-gray-400">
							<div className="text-center">
								<p className="text-lg font-medium mb-2">No cards yet</p>
								<p className="text-sm">Click "Add Card" to get started</p>
							</div>
						</div>
					)}

					{dragState &&
						(() => {
							const draggedCard = dragState.previewCards.find(
								(c) => c.id === dragState.cardId
							);
							if (!draggedCard) return null;
							const pixelWidth =
								draggedCard.width * columnWidth +
								(draggedCard.width - 1) * config.horizontalMargin;
							const pixelHeight =
								draggedCard.height * config.rowHeight +
								(draggedCard.height - 1) * config.verticalMargin;
							const pixelX =
								draggedCard.x * (columnWidth + config.horizontalMargin);
							const pixelY =
								draggedCard.y * (config.rowHeight + config.verticalMargin);

							return (
								<div
									key={`shadow-${draggedCard.id}`}
									className="absolute bg-blue-100 border-2 border-blue-300 border-dashed transition-all duration-200 opacity-50"
									style={{
										width: `${pixelWidth}px`,
										height: `${pixelHeight}px`,
										transform: `translate(${pixelX}px, ${pixelY}px)`,
										pointerEvents: 'none',
										borderRadius: '14px',
									}}
								/>
							);
						})()}

					{finalDisplayCards.map((card) => {
						const isDragging = dragState?.cardId === card.id;

						return (
							<Card
								key={card.id}
								card={card}
								onDelete={() => deleteCard(card.id)}
								onResize={(w, h) => handleCardResize(card.id, w, h)}
								onResizeStart={handleResizeStart}
								onResizeEnd={handleResizeEnd}
								onDragMove={(px, py) => handleDragMove(card.id, px, py)}
								onDragEnd={(gx, gy) => handleDragEnd(card.id, gx, gy)}
								onUpdateCardData={onUpdateCardData}
								dragOffset={isDragging ? dragState?.offset : undefined}
								columnWidth={columnWidth}
								rowHeight={config.rowHeight}
								horizontalMargin={config.horizontalMargin}
								verticalMargin={config.verticalMargin}
								maxColumns={currentColumns}
								isLocked={!isDesktopView}
								isEditMode={isEditMode}
								setLastResolvedCards={setLastResolvedCards}
								userStats={userStats}
								announcements={announcements}
								leaderboards={leaderboards}
								userStatsLoading={userStatsLoading}
								isAdminView={isAdminView}
								currentUser={currentUser}
								setCurrentUser={setCurrentUser}
								departments={departments}
								subCompanies={subCompanies}
								jobNotificationSetting={jobNotificationSetting}
								hideSubCompany={hideSubCompany}
								hotSearchedJobs={hotSearchedJobs}
								recognitionShoutouts={recognitionShoutouts}
								myRewards={myRewards}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}
